import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const SideMenuContext = createContext(undefined);

const normalizeMainMenuIndex = (value) => {
	const index = Number(value);
	if (!Number.isInteger(index)) return -1;
	if (index < -1 || index > 1) return -1;
	return index;
};

const normalizeViewIndex = (value) => {
	const index = Number(value);
	if (!Number.isInteger(index)) return -1;
	if (index < -1 || index > 1) return -1;
	return index;
};

const normalizeContentIndex = (value) => {
	const index = Number(value);
	if (!Number.isInteger(index)) return -1;
	if (index < -1 || index > 3) return -1;
	return index;
};

export const SideMenuProvider = ({ children }) => {
	const [if_show_side_menu, set_if_show_side_menu] = useState(true);
	const [if_show_view_option, set_if_show_view_option_state] = useState(true);
	const [if_show_content_option, set_if_show_content_option_state] = useState(true);
	const [index_btn_main_menus_selected, set_index_btn_main_menus_selected_state] = useState(-1);
	const [index_view_btn_selected, set_index_view_btn_selected_state] = useState(0);
	const [index_content_btn_selected, set_index_content_btn_selected_state] = useState(0);

	const set_if_show_view_option = useCallback((nextValue) => {
		set_if_show_view_option_state(Boolean(nextValue));
	}, []);

	const set_if_show_content_option = useCallback((nextValue) => {
		set_if_show_content_option_state(Boolean(nextValue));
	}, []);

	const set_index_btn_main_menus_selected = useCallback((nextValue) => {
		set_index_btn_main_menus_selected_state((prev) => {
			const normalized = normalizeMainMenuIndex(nextValue);
			return prev === normalized ? prev : normalized;
		});
	}, []);

	const set_index_view_btn_selected = useCallback((nextValue) => {
		set_index_view_btn_selected_state((prev) => {
			const normalized = normalizeViewIndex(nextValue);
			return prev === normalized ? prev : normalized;
		});
	}, []);

	const set_index_content_btn_selected = useCallback((nextValue) => {
		set_index_content_btn_selected_state((prev) => {
			const normalized = normalizeContentIndex(nextValue);
			return prev === normalized ? prev : normalized;
		});
	}, []);

	const select_main_menu = useCallback((nextIndex) => {
		const normalized = normalizeMainMenuIndex(nextIndex);
		set_index_btn_main_menus_selected_state((current) => {
			return current === normalized ? current : normalized;
		});
	}, []);

	const value = useMemo(
		() => ({
			if_show_side_menu,
			set_if_show_side_menu,
			if_show_view_option,
			set_if_show_view_option,
			if_show_content_option,
			set_if_show_content_option,
			index_btn_main_menus_selected,
			select_main_menu,
			set_index_btn_main_menus_selected,
			index_view_btn_selected,
			set_index_view_btn_selected,
			index_content_btn_selected,
			set_index_content_btn_selected,
		}),
		[
			if_show_side_menu,
			if_show_view_option,
			if_show_content_option,
			index_btn_main_menus_selected,
			select_main_menu,
			set_index_btn_main_menus_selected,
			index_view_btn_selected,
			set_index_view_btn_selected,
			index_content_btn_selected,
			set_index_content_btn_selected,
		]
	);

	return <SideMenuContext.Provider value={value}>{children}</SideMenuContext.Provider>;
};

export const useSideMenuContext = () => {
	const context = useContext(SideMenuContext);

	if (!context) {
		throw new Error('useSideMenuContext must be used within a SideMenuProvider.');
	}

	return context;
};

export default SideMenuContext;
